import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import PaymentForm from "./PaymentForm";
import { formatPrice } from "./config/prices";

const loadRazorpay = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve();
    document.body.appendChild(script);
  });
};

export default function Rezorpay({ amount: propAmount = 5999, onSuccess }) {
  const { amount: urlAmount, course: urlCourse } = useParams();
  const amount = urlAmount ? parseInt(urlAmount) : propAmount;
  const course = urlCourse || 'UNKNOWN_COURSE';
  const [loading, setLoading] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  useEffect(() => {
    loadRazorpay().then(() => setScriptLoaded(true));
  }, []);

  const onPay = () => {
    setShowPaymentForm(true);
  };

  const handleProceedToPayment = (formData) => {
    setShowPaymentForm(false);
    initiateRazorpayPayment(formData);
  };

  const initiateRazorpayPayment = (formData) => {
    if (!scriptLoaded) {
      alert('Razorpay script not loaded yet. Please try again.');
      return;
    }
    setLoading(true);

    const options = {
      key: process.env.REACT_APP_RAZORPAY_MODE === 'live'
        ? process.env.REACT_APP_RAZORPAY_LIVE_KEY_ID
        : process.env.REACT_APP_RAZORPAY_TEST_KEY_ID,
      amount: amount * 100, // Amount in paisa (multiply by 100 for INR)
      currency: 'INR',
      name: 'Lakshya Group of Institutions',
      description: `Payment for ${course.replace(/_/g, ' ')} course - ${formatPrice(amount)}`,
      handler: function (response) {
        setLoading(false);
        // Include course information in the response
        const enhancedResponse = {
          ...response,
          course: course,
          amount: amount,
          courseName: course.replace(/_/g, ' ')
        };
        if (onSuccess) onSuccess(enhancedResponse);
      },
      prefill: {
        name: formData.name,
        email: formData.email,
        contact: formData.phone
      },
      theme: {
        color: '#667eea'
      },
      modal: {
        ondismiss: function() {
          setLoading(false);
        }
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  return (
    <>
      <style>{`
        .pay-box {
          max-width: 520px;
          margin: 60px auto;
          padding: 28px;
          border-radius: 18px;
          background: rgba(255,255,255,0.07);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(255,255,255,0.15);
          box-shadow: 0 20px 40px rgba(0,0,0,0.45);
          color: #0f0e0ed0;
          font-weight: 700;
          animation: fadeUp 0.7s ease both;
          text-align: center;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .pay-button {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          color: white;
          font-size: 16px;
          cursor: pointer;
          transition: transform 0.2s;
          margin-top: 20px;
        }

        .pay-button:hover {
          transform: translateY(-2px);
        }

        .pay-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .status {
          margin-top: 20px;
          font-size: 14px;
        }
      `}</style>
      <div className="pay-box">
        <h2>Complete Your Payment</h2>
        {process.env.REACT_APP_RAZORPAY_MODE === 'test' && (
          <div style={{
            background: '#ffeb3b',
            color: '#000',
            padding: '8px',
            borderRadius: '4px',
            marginBottom: '10px',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            ⚠️ TEST MODE - This is a demo payment
          </div>
        )}
        <p>Course: {course.replace(/_/g, ' ')}</p>
        <p>Amount: {formatPrice(amount)}</p>
        <button
          className="pay-button"
          onClick={onPay}
          disabled={loading || !scriptLoaded}
        >
          {loading ? 'Processing...' : 'Proceed to Payment'}
        </button>
        {loading && <div className="status">Opening payment gateway...</div>}
      </div>

      {/* Payment Form Modal */}
      {showPaymentForm && (
        <PaymentForm
          amount={amount}
          course={course}
          onClose={() => setShowPaymentForm(false)}
          onProceedToPayment={handleProceedToPayment}
        />
      )}
    </>
  );
}
